// OPERATOR ALERT POPUP — "PRO" variant (Jose 2026-10-24): an EXACT clone of the Emergency popup's
// top half (freestanding SLIK head logo + concentric pulsing rings + a badge over the rings), with the
// bottom card swapped to the one-brain alert message + actions. Colour-driven per type (green for
// arrivals). This is version TWO — the boxed `OperatorAlertPopup` is KEPT for comparison; App.tsx
// mounts one or the other. Reuses the Emergency CSS for the pulseRing/pulse keyframes + overlay.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { UserCheck, Bell, MessageSquare, Clock, Play } from 'lucide-react';
import toast from 'react-hot-toast';
import { usePinLock } from '../../contexts/PinLockContext';
import { appointmentService } from '../../services/appointmentService';
import {
  subscribeOperatorAlerts, dismissOperatorAlert, OperatorAlert, OperatorAlertType,
} from '../../services/operatorAlertService';
import './EmergencyBookingPopupNotification.css'; // pulseRing / pulse / emerge keyframes + overlay

interface TypeStyle {
  color: string; glow: string; label: string;
  Icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
}
const TYPE_STYLE: Record<OperatorAlertType, TypeStyle> = {
  arrival:   { color: '#22c55e', glow: 'rgba(34,197,94,0.5)',  label: 'ARRIVED',  Icon: UserCheck },
  emergency: { color: '#ef4444', glow: 'rgba(239,68,68,0.5)',  label: 'URGENT',   Icon: Bell },
  message:   { color: '#3b82f6', glow: 'rgba(59,130,246,0.5)', label: 'MESSAGE',  Icon: MessageSquare },
  waitlist:  { color: '#f59e0b', glow: 'rgba(245,158,11,0.5)', label: 'WAITLIST', Icon: Clock },
};

function chime() {
  try {
    const Ctx = (window as any).AudioContext || (window as any).webkitAudioContext;
    if (Ctx) {
      const ctx = new Ctx();
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination);
      o.type = 'sine'; o.frequency.setValueAtTime(880, ctx.currentTime);
      o.frequency.setValueAtTime(1175, ctx.currentTime + 0.12);
      g.gain.setValueAtTime(0.0001, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.03);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.4);
      o.start(); o.stop(ctx.currentTime + 0.42);
    }
  } catch { /* non-fatal */ }
  try { (navigator as any).vibrate?.([120, 60, 120]); } catch { /* non-fatal */ }
}

export const OperatorAlertPopupPro: React.FC = () => {
  const { currentBarber } = usePinLock();
  const [alerts, setAlerts] = useState<OperatorAlert[]>([]);
  const [handled, setHandled] = useState<Set<string>>(new Set());
  const [logoOk, setLogoOk] = useState(true);
  const lastShownId = useRef<string | null>(null);

  useEffect(() => subscribeOperatorAlerts(setAlerts), []);

  const mineUnhandled = useMemo(() => {
    const myId = (currentBarber as any)?.barberId || (currentBarber as any)?.id || null;
    return alerts.filter((a) => {
      if (handled.has(a.id)) return false;
      // EMERGENCY keeps its own rich popup (EmergencyBookingPopupNotification) for the in-app surface — its
      // operatorAlerts doc exists only to drive the barber phone-push (notifyBarberOnAlert). Don't double-pop.
      if (a.type === 'emergency') return false;
      if (a.target === 'shop') return true;
      return !!a.barberId && !!myId && a.barberId === myId;
    });
  }, [alerts, currentBarber, handled]);
  const active = mineUnhandled[0] || null;
  // How many MORE route to me behind this one (e.g. several waitlist joins) — one-at-a-time never hides a stack.
  const queuedBehind = Math.max(0, mineUnhandled.length - 1);

  useEffect(() => {
    if (active && active.id !== lastShownId.current) { lastShownId.current = active.id; chime(); }
    if (!active) lastShownId.current = null;
  }, [active]);

  if (!active) return null;

  const style = TYPE_STYLE[active.type] || TYPE_STYLE.arrival;
  const { Icon } = style;
  const hide = (id: string) => setHandled((prev) => new Set(prev).add(id));
  const onDismiss = () => { hide(active.id); dismissOperatorAlert(active.id); };
  const onStart = async () => {
    const apptId = active.appointmentId;
    hide(active.id); dismissOperatorAlert(active.id);
    if (apptId) {
      try {
        await appointmentService.updateAppointment(apptId, { status: 'in-progress' as any });
        toast.success(`▶ Started ${active.clientName || 'service'}`);
      } catch { toast.error('Could not start the service'); }
    }
  };

  const ring = (delay: string): React.CSSProperties => ({
    position: 'absolute', width: '100%', height: '100%', borderRadius: '50%',
    border: `3px solid ${style.color}`, animation: 'pulseRing 2s ease-out infinite', animationDelay: delay,
  });

  return (
    <div className="emergency-popup-overlay" onClick={onDismiss} style={{ zIndex: 100050 }}>
      <div className="emergency-popup-container" onClick={(e) => e.stopPropagation()}>
        {/* badge over the rings (mirrors URGENT) */}
        <div
          style={{
            position: 'absolute', top: -10, left: '50%', transform: 'translateX(-50%)',
            background: style.color, color: '#0b1220', fontSize: 12, fontWeight: 800, letterSpacing: 2,
            padding: '6px 20px', borderRadius: 20, boxShadow: `0 4px 15px ${style.glow}`, zIndex: 10,
          }}
        >
          {style.label}
        </div>

        {/* FREESTANDING head logo + concentric pulsing rings (exact Emergency structure, colour-swapped) */}
        <div style={{ position: 'relative', width: 200, height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20 }}>
          <div style={ring('0s')} />
          <div style={ring('0.5s')} />
          <div style={ring('1s')} />
          <div
            style={{
              position: 'relative', width: 160, height: 160, borderRadius: '50%', overflow: 'hidden',
              background: 'white', border: `4px solid ${style.color}`,
              boxShadow: `0 0 0 8px ${style.glow.replace('0.5', '0.3')}, 0 0 30px ${style.glow}`,
              animation: 'pulse 2s ease-in-out infinite', zIndex: 5,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            {logoOk ? (
              <img
                src="./emergency-barber-logo.jpg"
                alt="SLIK"
                style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center top' }}
                onError={() => setLogoOk(false)}
              />
            ) : (
              <Icon className="w-16 h-16" style={{ color: style.color }} />
            )}
          </div>
        </div>

        {/* bottom card — the one-brain alert message + actions (dark, matches the Emergency card) */}
        <div
          style={{
            background: '#1e293b', borderRadius: 16, padding: '20px 24px', minWidth: 300, maxWidth: 340,
            textAlign: 'center', boxShadow: '0 10px 40px rgba(0,0,0,0.45)', border: `1px solid ${style.color}33`,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 10 }}>
            <Icon className="w-5 h-5" style={{ color: style.color }} />
            <span style={{ color: style.color, fontWeight: 800, fontSize: 18 }}>{active.title}</span>
          </div>
          {active.clientName && (
            <p style={{ margin: '2px 0 0', fontSize: 20, fontWeight: 800, color: '#ffffff' }}>{active.clientName}</p>
          )}
          {(active.time || active.body) && (
            <p style={{ margin: '6px 0 0', fontSize: 13, color: '#94a3b8' }}>
              {active.body || (active.time ? `${active.time} appointment` : '')}
            </p>
          )}

          {queuedBehind > 0 && (
            <p style={{ margin: '10px 0 0', fontSize: 12, fontWeight: 700, letterSpacing: 0.3, color: style.color }}>
              +{queuedBehind} more waiting · dismiss to see {queuedBehind === 1 ? 'it' : 'them'}
            </p>
          )}

          <div style={{ display: 'flex', gap: 12, marginTop: 18 }}>
            {active.type === 'arrival' && active.appointmentId ? (
              <>
                <button
                  onClick={onStart}
                  className="touch-manipulation"
                  style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '13px', borderRadius: 12, fontWeight: 800, color: '#0b1220', background: style.color, border: 'none' }}
                >
                  <Play className="w-4 h-4" /> Start service
                </button>
                <button
                  onClick={onDismiss}
                  className="touch-manipulation"
                  style={{ padding: '13px 18px', borderRadius: 12, fontWeight: 700, color: '#cbd5e1', background: 'rgba(255,255,255,0.08)', border: 'none' }}
                >
                  Not yet
                </button>
              </>
            ) : (
              <button
                onClick={onDismiss}
                className="touch-manipulation"
                style={{ flex: 1, padding: '13px', borderRadius: 12, fontWeight: 800, color: '#0b1220', background: style.color, border: 'none' }}
              >
                Got it
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OperatorAlertPopupPro;
